<template>
  <v-main class="fc-page">
    <v-container class="py-6">
      <div class="fc-hero mb-6">
        <div class="d-flex align-center">
          <v-avatar size="52" class="fc-hero__icon mr-4">
            <v-icon dark size="28">mdi-file-excel-outline</v-icon>
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold white--text">นำเข้าข้อมูลจาก Funcrowd</div>
            <div class="fc-hero__sub">
              อัปโหลดไฟล์รายงานที่ export จาก Funcrowd ตรวจผลก่อน แล้วค่อยยืนยันนำเข้า
            </div>
          </div>
          <v-spacer />
          <v-btn text small color="white" class="d-none d-md-flex" to="/Tool/Migration">
            <v-icon left small>mdi-arrow-left</v-icon>
            ศูนย์รวมการย้ายข้อมูล
          </v-btn>
        </div>
      </div>

      <v-stepper v-model="step" vertical class="fc-stepper">
        <v-stepper-step :complete="step > 1" step="1">
          เลือกร้านปลายทาง
          <small v-if="form.shopId">{{ shopName }}</small>
        </v-stepper-step>
        <v-stepper-content step="1">
          <v-select
            v-model="form.shopId"
            :items="shops"
            item-text="name"
            item-value="shopId"
            label="ร้าน"
            outlined
            dense
            :loading="loadingShops"
          />
          <v-alert v-if="shopInfo" type="info" text dense>
            คะแนนที่นำเข้าจะหมดอายุใน {{ shopInfo.monthExpire || '-' }} เดือน นับจากวันที่นำเข้า
            ตามการตั้งค่าของร้านนี้
          </v-alert>

          <div class="caption grey--text text--darken-1 mb-1">นำเข้าไปที่ระบบไหน</div>
          <v-radio-group v-model="form.target" row dense class="mt-0">
            <v-radio label="Loyalty V.1 (ระบบนี้)" value="v1" />
            <v-radio label="Loyalty V.2" value="v2" />
            <v-radio label="ทั้งสองระบบ" value="both" />
          </v-radio-group>
          <v-alert v-if="form.target !== 'v1'" type="warning" text dense>
            การนำเข้าไป V.2 ยังย้อนกลับอัตโนมัติไม่ได้ ปุ่มย้อนกลับจะคืนเฉพาะข้อมูลฝั่ง V.1
          </v-alert>

          <v-btn color="primary" :disabled="!form.shopId" @click="step = 2">ถัดไป</v-btn>
          <v-btn
            text
            small
            color="error"
            class="ml-2"
            :disabled="!form.shopId"
            @click="openShopRollback"
          >
            ล้างข้อมูลที่เคยนำเข้าของร้านนี้
          </v-btn>
        </v-stepper-content>

        <v-stepper-step :complete="step > 2" step="2">
          อัปโหลดไฟล์
          <small v-if="files.length">{{ files.length }} ไฟล์</small>
        </v-stepper-step>
        <v-stepper-content step="2">
          <v-file-input
            v-model="files"
            label="ไฟล์รายงานจาก Funcrowd (.xlsx)"
            accept=".xlsx,.xlsm"
            class="fc-file"
            multiple
            small-chips
            outlined
            show-size
          />
          <div class="caption grey--text mb-3">
            ต้องมีครบ 4 ไฟล์: รายงานข้อมูลลูกค้า, รายงานการใช้งาน,
            แพ็กเกจพิเศษตามรายชื่อ, แพ็กเกจพิเศษตามความเคลื่อนไหว
          </div>
          <v-btn text @click="step = 1">ย้อนกลับ</v-btn>
          <v-btn
            color="primary"
            :disabled="!files.length"
            :loading="checking"
            @click="runValidate"
          >
            ตรวจสอบไฟล์
          </v-btn>
        </v-stepper-content>

        <v-stepper-step :complete="step > 3" step="3">ตรวจผลก่อนนำเข้า</v-stepper-step>
        <v-stepper-content step="3">
          <div v-if="report">
            <v-alert v-if="report.blocking" type="error" text dense>
              พบข้อผิดพลาด {{ report.blocking }} รายการ ต้องแก้ไฟล์ก่อนจึงจะนำเข้าได้
            </v-alert>
            <v-alert v-else type="success" text dense>
              ไฟล์ผ่านการตรวจสอบ พร้อมนำเข้า
            </v-alert>

            <v-row v-if="report.overwrite && !report.overwrite.skipped" class="mb-2">
              <v-col
                v-for="tile in tiles"
                :key="tile.label"
                cols="6"
                md="3"
              >
                <div class="fc-tile" :class="'fc-tile--' + tile.tone">
                  <v-icon :color="tile.color" size="20">{{ tile.icon }}</v-icon>
                  <div class="fc-tile__value" :class="tile.color + '--text'">{{ tile.value }}</div>
                  <div class="fc-tile__label">{{ tile.label }}</div>
                </div>
              </v-col>
            </v-row>

            <v-simple-table dense class="fc-table mb-4">
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td>สมาชิกในไฟล์</td>
                    <td class="text-right">{{ report.members.from_member_file }}</td>
                  </tr>
                  <tr>
                    <td>เบอร์ที่พบเฉพาะในไฟล์ความเคลื่อนไหว</td>
                    <td class="text-right">{{ report.members.pending_line_count }}</td>
                  </tr>
                  <tr>
                    <td>เบอร์ซ้ำในไฟล์ / เบอร์ผิดรูปแบบ</td>
                    <td class="text-right">
                      {{ report.members.duplicate_phone.length }} /
                      {{ report.members.invalid_phone.length }}
                    </td>
                  </tr>
                  <tr>
                    <td>รวมคะแนนที่จะนำเข้า</td>
                    <td class="text-right">{{ report.members.sum_current_point }}</td>
                  </tr>
                  <tr v-if="report.masters && !report.masters.skipped">
                    <td>แพ็กเกจ ใช้ของเดิม / สร้างใหม่</td>
                    <td class="text-right">
                      {{ report.masters.package_reuse.length }} /
                      {{ report.masters.package_create.length }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-alert
              v-if="lineUidConflict.length"
              type="warning"
              text
              dense
            >
              มี {{ lineUidConflict.length }} เบอร์ที่ไอดีไลน์ในไฟล์ไม่ตรงกับในระบบ
              ตรวจก่อนนำเข้าเพื่อไม่ให้ประวัติไปลงผิดคน
            </v-alert>

            <v-expansion-panels v-if="report.issues.length" flat class="mb-3">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  รายการเตือน {{ report.issues.length }} รายการ
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(issue, i) in report.issues.slice(0, 200)" :key="i" class="caption">
                    <span :class="issue.level === 'error' ? 'red--text' : 'orange--text'">
                      [{{ issue.level }}]
                    </span>
                    {{ issue.detail }}
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <v-btn text @click="step = 2">ย้อนกลับ</v-btn>
          <v-btn
            color="secondary"
            class="ml-2"
            :loading="running"
            :disabled="!canImport"
            @click="runImport(true)"
          >
            ทดลองนำเข้า (ไม่บันทึก)
          </v-btn>
          <v-btn
            color="primary"
            class="ml-2"
            :disabled="!canImport"
            @click="confirmDialog = true"
          >
            ยืนยันนำเข้า
          </v-btn>
        </v-stepper-content>

        <v-stepper-step :complete="step > 4" step="4">ผลการนำเข้า</v-stepper-step>
        <v-stepper-content step="4">
          <div v-if="batch">
            <v-alert :type="batch.status === 'done' ? 'success' : 'info'" text dense>
              {{ batch.status === 'done' ? 'นำเข้าเรียบร้อย' : 'ทดลองนำเข้าเรียบร้อย ยังไม่บันทึกลงระบบ' }}
              — รหัสงาน {{ batch.batchId }}
            </v-alert>
            <v-simple-table dense class="fc-table mb-4">
              <template v-slot:default>
                <tbody>
                  <tr v-for="(value, key) in batch.summary.counts" :key="key">
                    <td>{{ countLabel(key) }}</td>
                    <td class="text-right">{{ value }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div v-if="batch.summary.createdPackages.length" class="mb-3">
              <div class="caption grey--text">แพ็กเกจที่ถูกสร้างใหม่</div>
              <div v-for="pkg in batch.summary.createdPackages" :key="pkg.packageId" class="caption">
                #{{ pkg.packageId }} {{ pkg.name }}
              </div>
            </div>
            <v-btn
              v-if="batch.status === 'done' && batch.target !== 'v2'"
              color="error"
              outlined
              :loading="rollingBack"
              @click="openBatchRollback"
            >
              ย้อนกลับการนำเข้าครั้งนี้
            </v-btn>
            <v-btn text class="ml-2" @click="resetAll">เริ่มใหม่</v-btn>
          </div>
        </v-stepper-content>
      </v-stepper>
    </v-container>

    <v-dialog v-model="confirmDialog" max-width="460" persistent>
      <v-card>
        <div class="fc-dialog__title text-subtitle-1 font-weight-bold">ยืนยันนำเข้าข้อมูลจริง</div>
        <div class="fc-dialog__body">
          <div class="body-2 mb-3">
            ข้อมูลจะถูกบันทึกลงร้าน {{ shopName }} ทันที ย้อนกลับได้จากหน้านี้หลังนำเข้าเสร็จ
          </div>
          <v-text-field v-model="form.userName" label="ผู้ใช้ของร้าน" outlined dense />
          <v-text-field
            v-model="form.userPassword"
            label="รหัสผ่าน"
            type="password"
            outlined
            dense
          />
        </div>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDialog = false">ยกเลิก</v-btn>
          <v-btn
            color="primary"
            :loading="running"
            :disabled="!form.userName || !form.userPassword"
            @click="runImport(false)"
          >
            นำเข้า
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rollbackDialog" max-width="460" persistent>
      <v-card>
        <div class="fc-dialog__title text-subtitle-1 font-weight-bold">
          {{ rollbackScope === 'shop' ? 'ล้างข้อมูลที่เคยนำเข้า' : 'ย้อนกลับการนำเข้า' }}
        </div>
        <div class="fc-dialog__body">
          <div v-if="rollbackScope === 'shop'" class="body-2 mb-3">
            ระบบจะเอาทุกแถวที่การนำเข้าจาก Funcrowd เคยสร้างไว้ในร้าน {{ shopName }} ออกทั้งหมด
            คะแนนของสมาชิกเดิมที่เคยถูกทับจะคืนให้ไม่ได้
          </div>
          <div v-else class="body-2 mb-3">
            ระบบจะลบทุกแถวที่สร้างจากงานนี้ และคืนคะแนนเดิมของสมาชิกที่ถูกทับ
          </div>
          <v-text-field v-model="form.userName" label="ผู้ใช้ของร้าน" outlined dense />
          <v-text-field
            v-model="form.userPassword"
            label="รหัสผ่าน"
            type="password"
            outlined
            dense
          />
        </div>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rollbackDialog = false">ยกเลิก</v-btn>
          <v-btn
            color="error"
            :loading="rollingBack"
            :disabled="!form.userName || !form.userPassword"
            @click="doRollback"
          >
            ย้อนกลับ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from 'axios'

const COUNT_LABELS = {
  memberInserted: 'สมาชิกใหม่ที่สร้าง',
  memberOverwritten: 'สมาชิกเดิมที่คะแนนถูกทับ',
  pendingAttached: 'สมาชิกเดิมที่นำประวัติไปต่อ',
  pendingCreated: 'สมาชิกรอผูกไลน์ที่สร้าง',
  openingBucket: 'ก้อนคะแนนยกยอด',
  pointLedger: 'ประวัติคะแนน',
  packageCreated: 'แพ็กเกจที่สร้างใหม่',
  packageGrant: 'สิทธิ์แพ็กเกจของสมาชิก',
  packageMovement: 'ประวัติการใช้แพ็กเกจ',
  packageMovementWithoutGrant: 'ความเคลื่อนไหวที่ไม่มีสิทธิ์ตั้งต้น',
  pointLedgerSkipped: 'ประวัติคะแนนที่ข้าม',
  packageGrantSkipped: 'สิทธิ์แพ็กเกจที่ข้าม',
  packageMovementSkipped: 'ประวัติแพ็กเกจที่ข้าม'
}

export default {
  data () {
    return {
      step: 1,
      shops: [],
      shopInfo: null,
      loadingShops: false,
      files: [],
      report: null,
      batch: null,
      checking: false,
      running: false,
      rollingBack: false,
      confirmDialog: false,
      rollbackDialog: false,
      rollbackScope: 'batch',
      form: { shopId: '', target: 'v1', userName: '', userPassword: '' }
    }
  },
  computed: {
    tiles () {
      const over = (this.report && this.report.overwrite) || {}
      return [
        {
          value: over.will_insert_count,
          label: 'สมาชิกใหม่ที่จะถูกสร้าง',
          icon: 'mdi-account-plus-outline',
          color: 'primary',
          tone: 'blue'
        },
        {
          value: over.will_overwrite_count,
          label: 'สมาชิกเดิมที่คะแนนจะถูกทับ',
          icon: 'mdi-alert-outline',
          color: 'orange',
          tone: 'orange'
        },
        {
          value: over.pending_attach_count,
          label: 'สมาชิกเดิมที่จะนำประวัติไปต่อ',
          icon: 'mdi-history',
          color: 'teal',
          tone: 'teal'
        },
        {
          value: over.untouched_count,
          label: 'สมาชิกเดิมที่ไม่ถูกแตะ',
          icon: 'mdi-shield-check-outline',
          color: 'grey',
          tone: 'grey'
        }
      ]
    },
    shopName () {
      const hit = this.shops.find(row => row.shopId === this.form.shopId)
      return hit ? hit.name : this.form.shopId
    },
    canImport () {
      return !!this.report && !this.report.blocking
    },
    lineUidConflict () {
      if (!this.report || !this.report.overwrite || this.report.overwrite.skipped) return []
      return this.report.overwrite.line_uid_conflict || []
    }
  },
  watch: {
    'form.shopId' (value) {
      this.shopInfo = null
      if (!value) return
      axios.get(this.DNS_IP + '/importer/shop/' + value).then(res => {
        if (res.data.status) this.shopInfo = res.data.data
      })
    }
  },
  mounted () {
    this.loadShops()
  },
  methods: {
    countLabel (key) {
      return COUNT_LABELS[key] || key
    },
    loadShops () {
      this.loadingShops = true
      axios
        .get(this.DNS_IP + '/importer/shops')
        .then(res => {
          if (res.data.status) this.shops = res.data.data
        })
        .finally(() => {
          this.loadingShops = false
        })
    },
    buildForm (extra) {
      const data = new FormData()
      this.files.forEach(file => data.append('files', file))
      data.append('shopId', this.form.shopId)
      data.append('target', this.form.target)
      Object.keys(extra || {}).forEach(key => data.append(key, extra[key]))
      return data
    },
    runValidate () {
      this.checking = true
      this.report = null
      axios
        .post(this.DNS_IP + '/importer/funcrowd/validate', this.buildForm())
        .then(res => {
          if (!res.data.status) {
            this.$swal('ไม่สำเร็จ', res.data.message, 'error')
            return
          }
          this.report = res.data.data
          this.step = 3
        })
        .catch(() => this.$swal('ไม่สำเร็จ', 'ตรวจไฟล์ไม่สำเร็จ', 'error'))
        .finally(() => {
          this.checking = false
        })
    },
    runImport (dryRun) {
      this.running = true
      const extra = { dryRun: dryRun ? '1' : '0' }
      if (!dryRun) {
        extra.userName = this.form.userName
        extra.userPassword = this.form.userPassword
      }
      axios
        .post(this.DNS_IP + '/importer/funcrowd/run', this.buildForm(extra))
        .then(res => {
          if (!res.data.status) {
            this.$swal('ไม่สำเร็จ', res.data.message, 'error')
            return
          }
          this.batch = res.data.batch
          this.confirmDialog = false
          this.step = 4
        })
        .catch(() => this.$swal('ไม่สำเร็จ', 'นำเข้าไม่สำเร็จ', 'error'))
        .finally(() => {
          this.running = false
        })
    },
    openBatchRollback () {
      this.rollbackScope = 'batch'
      this.rollbackDialog = true
    },
    openShopRollback () {
      this.rollbackScope = 'shop'
      this.rollbackDialog = true
    },
    doRollback () {
      this.rollingBack = true
      const data = new FormData()
      data.append('userName', this.form.userName)
      data.append('userPassword', this.form.userPassword)
      const url = this.rollbackScope === 'shop'
        ? '/importer/rollback-shop/' + this.form.shopId
        : '/importer/rollback/' + this.batch.batchId
      axios
        .post(this.DNS_IP + url, data)
        .then(res => {
          if (!res.data.status) {
            this.$swal('ไม่สำเร็จ', res.data.message, 'error')
            return
          }
          this.rollbackDialog = false
          this.$swal('เรียบร้อย', 'ย้อนกลับการนำเข้าแล้ว', 'success')
          this.resetAll()
          this.rollbackScope = 'batch'
        })
        .catch(() => this.$swal('ไม่สำเร็จ', 'ย้อนกลับไม่สำเร็จ', 'error'))
        .finally(() => {
          this.rollingBack = false
        })
    },
    resetAll () {
      this.step = 1
      this.files = []
      this.report = null
      this.batch = null
      this.form.userPassword = ''
    }
  }
}
</script>

<style scoped>
.fc-page {
  background: #f6f7fb;
  min-height: 100%;
}

.fc-hero {
  background: linear-gradient(135deg, #00796b 0%, #26a69a 60%, #4db6ac 100%);
  border-radius: 16px;
  padding: 26px 30px;
  box-shadow: 0 6px 18px rgba(0, 121, 107, 0.22);
}

.fc-hero__icon {
  background: rgba(255, 255, 255, 0.18);
}

.fc-hero__sub {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  margin-top: 2px;
}

.fc-stepper {
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08) !important;
}

.fc-stepper >>> .v-stepper__content {
  margin-right: 0;
  padding: 8px 24px 24px 24px;
}

.fc-stepper >>> .v-stepper__step {
  padding: 20px 24px;
}

.fc-stepper >>> .v-stepper__label {
  font-weight: 600;
}

.fc-stepper >>> .v-stepper__label small {
  color: #6b7280;
  margin-top: 4px;
}

.fc-stepper >>> .v-btn {
  border-radius: 8px;
  margin-bottom: 6px;
}

.fc-stepper >>> .v-alert {
  border-radius: 10px;
  padding: 12px 16px;
}

.fc-dialog__title {
  padding: 20px 24px 4px;
}

.fc-dialog__body {
  padding: 12px 24px 4px;
}

.fc-file >>> .v-input__slot {
  min-height: 56px;
  padding: 6px 12px !important;
}

.fc-file >>> .v-file-input__text {
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0;
}

.fc-tile {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  height: 100%;
  padding: 18px 14px;
  text-align: center;
}

.fc-tile--blue { background: #e8eefc; }
.fc-tile--orange { background: #fff4e5; }
.fc-tile--teal { background: #e3f4f1; }
.fc-tile--grey { background: #f1f3f6; }

.fc-tile__value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.3;
}

.fc-tile__label {
  color: #5f6368;
  font-size: 12px;
  line-height: 1.5;
}

.fc-table {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  overflow-x: auto;
}

.fc-table >>> tbody tr:nth-child(even) {
  background: #fafbfc;
}

.fc-table >>> td {
  font-size: 13px;
  padding: 12px 18px !important;
}

.fc-table >>> td:last-child {
  font-weight: 600;
}

@media (max-width: 599px) {
  .fc-hero {
    padding: 18px 18px;
  }

  .fc-hero .text-h5 {
    font-size: 1.15rem !important;
  }

  .fc-stepper >>> .v-stepper__content {
    margin-left: 24px;
    margin-right: 0;
    padding: 8px 14px 20px 14px;
  }

  .fc-stepper >>> .v-stepper__step {
    padding: 16px 16px;
  }

  .fc-tile__value {
    font-size: 22px;
  }

  .fc-table >>> td {
    padding: 10px 12px !important;
  }

  .fc-stepper >>> .v-stepper__content .v-btn {
    margin-left: 0 !important;
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Tool/FuncrowdImport.vue